import { useState, useEffect } from "react";

import Loading from "../../layout/Loading";
import Form from "../../layout/Form";
import FormRow from "../../layout/FormRow";
import FormFieldText from "../../input/FormFieldText";
import FormFieldTags from "../../input/FormFieldTags";
import Button from "../../core/Button";

import useTriggeredAPICall from "../../../hooks/fetch-hooks/useTriggeredAPICall";
import { errorToast, successfulToast } from "../../../util/toastNotifications";
import { isValidUSZip, isValidPhoneNumber } from "../../../util/stringUtils";

const INITIAL_FORM = {
  name: "",
  address: "",
  city: "",
  state: "",
  zipCode: "",
  phone: "",
  country: "",
  tags: [],
};

const OrganizationForm = (props) => {
  const [formFields, setFormFields] = useState(INITIAL_FORM);
  const [formErrors, setFormErrors] = useState({});

  const orgId = props.match.params.orgId;

  const { fetchData, isLoading } = useTriggeredAPICall();

  const validateForm = () => {
    const errors = {};

    if (!formFields.name) {
      errors["name"] = "Please enter a name";
    }

    if (!isValidPhoneNumber(formFields.phone) && formFields.phone.length > 0) {
      errors["phone"] = "Please enter a valid phone number";
    }

    if (!isValidUSZip(formFields.zipCode) && formFields.zipCode.length > 0) {
      errors["zipCode"] = "Please enter a valid zip code";
    }

    return Object.keys(errors).length > 0 ? errors : null;
  };

  const handleFieldUpdate = (e) => {
    const { name, value } = e.target;

    setFormFields((prev) => ({ ...prev, [name]: value }));

    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const formValidation = validateForm();

    if (formValidation) {
      setFormErrors(formValidation);
      return;
    }

    const body = {
      name: formFields.name,
      address: formFields.address,
      city: formFields.city,
      state: formFields.state,
      zip_code: formFields.zipCode,
      country: formFields.country,
      phone: formFields.phone,
    };

    let fetchUrl = "/organization";
    let method = "POST";

    if (orgId) {
      fetchUrl = `/organization/${orgId}`;
      method = "PUT";
    }

    fetchData(fetchUrl, method, body)
      .then(() => {
        successfulToast(
          `Successfully ${orgId ? "updated" : "added"} organization`
        );

        props.history.push("/organizations");
      })
      .catch((err) => {
        console.error("Updating Org: ", err);

        errorToast(
          "Something went wrong, please check all the fields and resubmit"
        );
      });
  };

  const addTagToOrganization = (tagId) => {
    const body = {
      org_id: orgId,
      tag_id: tagId,
    };

    fetchData("/organization/tag", "POST", body)
      .then((result) => {
        setFormFields((prev) => ({ ...prev, tags: result.results.tags }));
      })
      .catch((err) => console.error("Add Tag to Organization", err));
  };

  const handleTagRemove = (tagId) => {
    const body = {
      org_id: orgId,
      tag_id: tagId,
    };

    fetchData("/organization/tag", "POST", body).then((result) =>
      setFormFields((prev) => ({ ...prev, tags: result.results.tags }))
    );
  };

  useEffect(() => {
    if (orgId) {
      fetchData(`/organization/${orgId}`, "GET").then((result) => {
        setFormFields({
          name: result.results.name,
          address: result.results.address,
          city: result.results.city,
          state: result.results.state,
          zipCode: result.results.zip_code,
          phone: result.results.phone,
          country: result.results.country,
          tags: result.results.tags,
        });
      });
    }
  }, [orgId, fetchData]);

  if (isLoading && orgId) {
    return <Loading />;
  }

  return (
    <div className="mt-5 flex center width-full">
      <div className="form-styling px-16 py-6 card">
        <h2>{orgId ? "Edit Organization" : "Add Organization"}</h2>

        <Form onSubmit={handleSubmit}>
          <FormFieldText
            name="name"
            label="Organization Name *"
            placeholder="Enter name..."
            type="text"
            maxLength="45"
            value={formFields.name}
            errorMessage={formErrors.name}
            onChange={handleFieldUpdate}
          ></FormFieldText>

          <FormFieldText
            name="address"
            label="Address"
            placeholder="Enter address..."
            type="text"
            maxLength="45"
            value={formFields.address}
            errorMessage={formErrors.address}
            onChange={handleFieldUpdate}
          ></FormFieldText>

          <FormRow>
            <FormFieldText
              name="city"
              label="City"
              placeholder="Enter city..."
              type="text"
              maxLength="45"
              value={formFields.city}
              errorMessage={formErrors.city}
              onChange={handleFieldUpdate}
              className="field-large"
            ></FormFieldText>

            <FormFieldText
              name="state"
              label="State"
              placeholder="Enter state..."
              type="text"
              maxLength="15"
              value={formFields.state}
              errorMessage={formErrors.state}
              onChange={handleFieldUpdate}
              className="field-medium"
            ></FormFieldText>

            <FormFieldText
              name="zipCode"
              label="Zip Code"
              placeholder="Enter zip/postal code..."
              type="text"
              value={formFields.zipCode}
              errorMessage={formErrors.zipCode}
              onChange={handleFieldUpdate}
              className="field-medium"
            ></FormFieldText>
          </FormRow>

          <FormRow>
            <FormFieldText
              name="country"
              label="Country"
              placeholder="Enter the country..."
              type="text"
              value={formFields.country}
              errorMessage={formErrors.country}
              onChange={handleFieldUpdate}
              className="field-large"
            ></FormFieldText>
          </FormRow>

          <FormRow>
            <FormFieldText
              name="phone"
              label="Phone"
              placeholder="Enter the phone number..."
              type="text"
              value={formFields.phone}
              errorMessage={formErrors.phone}
              onChange={handleFieldUpdate}
              className="field-medium"
            ></FormFieldText>
          </FormRow>

          {orgId && (
            <FormFieldTags
              selectedTags={formFields.tags}
              handleTagRemove={handleTagRemove}
              objectType="organizations"
              objectId={orgId}
              objectPk="org_id"
              handleAddTagToObject={addTagToOrganization}
            />
          )}

          <div className="button-row">
            <div className="button-row-left">
              <Button cancel>Cancel</Button>
            </div>
            <div className="button-row-right">
              <button type="submit">
                {orgId ? "Save Organization" : "Add Organization"}
              </button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default OrganizationForm;
